import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import jwt_decode from "jwt-decode";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { setUserId as setAmplitudeUserId } from "@amplitude/unified";
import purpleLogo from "../assets/purpleKnowtexLogo.svg";
import userJSON from "../utils/userJSON.js";
import sendServerLogging from "../utils/sendServerLogging.js";
import { markStandaloneUser } from "../utils/standaloneUser";
import { SESSION_STORAGE_BLOCKED_MESSAGE, setSessionToken } from "../lib/sessionToken";

const Login = ({ setAuth }) => {
  const [inputs, setInputs] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { email, password } = inputs;
  const errorProps = errorMessage
    ? { "aria-invalid": true, "aria-describedby": "login-error" }
    : {};

  const onChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  function sendEvent(userEvent) {
    // Replace "http://example.com/api/events" with the actual endpoint URL.
    const endpointUrl =
      "https://tpv4xmll56.execute-api.us-east-1.amazonaws.com/prod/doctorservice";

    axios
      .post(endpointUrl, userEvent)
      .then((response) => {
        //// console.log("Event sent successfully:", response.data);
      })
      .catch((error) => {
        //console.error("Failed to send event:", error);
      });
  }

  const onSubmitForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const body = { email, password };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );

      const parseRes = await response.json();

      if (parseRes.token) {
        if (!setSessionToken(parseRes.token)) {
          setAuth(false);
          toast.error(SESSION_STORAGE_BLOCKED_MESSAGE);
          setErrorMessage(SESSION_STORAGE_BLOCKED_MESSAGE);
          return;
        }
        localStorage.setItem("userName", parseRes.userName);
        localStorage.setItem("userEmail", normalizedEmail);
        if (parseRes.pylonEmailHash) {
          localStorage.setItem("pylonEmailHash", parseRes.pylonEmailHash);
        } else {
          localStorage.removeItem("pylonEmailHash");
        }
        markStandaloneUser(false);
        setAuth(true);
        toast.success("Login successful");

        // get user_id from token
        const decoded_token = jwt_decode(parseRes.token);
        const user_id = decoded_token["user"]["id"];

        //server logging
        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "LOGIN",
            eventData: {},
          },
        });

        // Amplitude
        setAmplitudeUserId(user_id);

        // Sentry
        Sentry.setUser({
          id: user_id,
          email: normalizedEmail,
        });

        // pylon
        window.pylon = {
          chat_settings: {
            app_id: "6000e45a-6b53-4401-93b4-a8d408e9b062",
            email: normalizedEmail,
            name: localStorage.getItem("userName"),
            ...(parseRes.pylonEmailHash
              ? { email_hash: parseRes.pylonEmailHash }
              : {}),
          },
        };


        //// console.log("User:%s log in succeed",email)
      } else {
        setAuth(false);
        const message =
          typeof parseRes === "string"
            ? parseRes
            : parseRes?.message || parseRes?.error || "Login failed";
        toast.error(message);
        setErrorMessage(message);
      }
    } catch (err) {
      console.error(err.message);
      const message = "We couldn't reach Knowtex. Check your connection and try again.";
      toast.error(message);
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    // Scrolls instead of clipping so the form stays reachable at 400% zoom.
    <main
      id="main-content"
      tabIndex={-1}
      className="relative w-dvw h-dvh flex flex-col items items-center overflow-x-hidden overflow-y-auto focus:outline-none"
    >
      <Helmet><title>Sign In – Knowtex</title></Helmet>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-left bg-auto bg-login-background -scale-x-100"
      />
      <img
        src={purpleLogo}
        alt="Knowtex"
        className="relative z-10 min-w-[20rem] max-w-[50rem] w-[30%] h-[40%] my-[1rem]"
      />
      <div className="relative z-10 min-w-[20rem] max-w-[50rem] bg-white rounded-md shadow-drop-shadow w-[30%] h-[45%] min-h-fit flex flex-col justify-center my-[-4rem]">
        <h1 className="text-ktx-navy text-[1.5rem] self-center mb-[4rem] font-medium">
          Login
        </h1>

        {/* Test button for sentry. Works locally but for PR purposes can comment back out to test  */}
        {/* <button
        type="button"
        onClick={() => {
          throw new Error("Sentry Test Error");
        }}
      >
        Break the world
      </button> */}
        <form
          className="flex flex-col items-center gap-[1.7rem]"
          onSubmit={onSubmitForm}
        >
          <div className="flex rounded-md bg-white shadow-knowtex-shadow w-[80%]">
            <svg
              className="self-center ml-[.4rem] w-6 h-6 text-light-grey dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 13h3.439a.991.991 0 0 1 .908.6 3.978 3.978 0 0 0 7.306 0 .99.99 0 0 1 .908-.6H20M4 13v6a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6M4 13l2-9h12l2 9"
              />
            </svg>
            <label htmlFor="email" className="sr-only">
              Email
            </label>
            <input
              type="email"
              placeholder="Email"
              name="email"
              className="text-accent1 placeholder:text-knowtex-grey py-[.7rem] w-[100%] italic border-none bg-none rounded-md"
              value={email}
              onChange={(e) => onChange(e)}
              id="email"
              autoComplete="username"
              required
              {...errorProps}
            />
          </div>
          <div className="flex rounded-md bg-white shadow-knowtex-shadow w-[80%]">
            <svg
              className="self-center ml-[.4rem] w-6 h-6 text-light-grey dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 12a28.076 28.076 0 0 1-1.091 9M7.231 4.37a8.994 8.994 0 0 1 12.88 3.73M2.958 15S3 14.577 3 12a8.949 8.949 0 0 1 1.735-5.307m12.84 3.088A5.98 5.98 0 0 1 18 12a30 30 0 0 1-.464 6.232M6 12a6 6 0 0 1 9.352-4.974M4 21a5.964 5.964 0 0 1 1.01-3.328 5.15 5.15 0 0 0 .786-1.926m8.66 2.486a13.96 13.96 0 0 1-.962 2.683M7.5 19.336C9 17.092 9 14.845 9 12a3 3 0 1 1 6 0c0 .749 0 1.521-.031 2.311M12 12c0 3 0 6-2 9"
              />
            </svg>
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <input
              type="password"
              name="password"
              placeholder="Password"
              className="text-accent1 placeholder:text-knowtex-grey py-[.7rem] w-[100%] italic border-none bg-none rounded-md"
              value={password}
              onChange={(e) => onChange(e)}
              id="password"
              autoComplete="current-password"
              required
              {...errorProps}
            />
          </div>
          <button
            type="submit"
            aria-label={loading ? "Logging in" : "Login"}
            disabled={loading}
            className={`flex items-center justify-center w-[80%] py-[.7rem] my-[1rem] rounded-md tracking-widest text-white ${
              loading
                ? "bg-accent2/70 cursor-not-allowed"
                : "bg-accent2 hover:bg-accent1"
            }`}
          >
            {loading && (
              <svg
                aria-hidden="true"
                className="w-5 h-5 mr-2 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            )}
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
        {/* Not a live region: the matching toast is already announced. */}
        <p
          id="login-error"
          className={errorMessage ? "w-[80%] self-center mb-4 text-sm font-semibold text-ktx-red" : "sr-only"}
        >
          {errorMessage}
        </p>
      </div>
    </main>
  );
};

export default Login;
